import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { notification } from "antd";
import { useIntl } from "react-intl";

import { InvitesHttpService } from "@/modules/candidate/services/InvitesHttpService";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import { getFriendlyErrorMessage } from "@/shared/services/http";
import { useLocale } from "@/i18n";
import { loadMessages } from "@/i18n/loadMessages";
import { pickAssessmentCopyVariant, useIsSurvey } from "@/modules/candidate/assessmentCopy";
import { contentLoadErrorMessages } from "../components/ContentLocaleError/contentLoadError.messages";
import { useInviteAvailableLanguagesHook } from "./useInviteAvailableLanguages.hook";

interface UseCandidateLocaleSelectionArgs {
  inviteId?: number;
  inviteLocale?: LanguagesIsoEnum | null;
  // True once the attempt has started (status past "accepted"). The language is locked per attempt,
  // so a started invite can neither auto-open the picker nor expose the "Change" link. Mirrors the
  // BE guard, which rejects PATCH /locale unless the invite is still "accepted".
  hasStarted?: boolean;
  onConfirmed?: (locale: LanguagesIsoEnum) => void;
  onSelected?: (locale: LanguagesIsoEnum) => void;
  isContentStale?: boolean;
  hasContentError?: boolean;
  preload?: (locale: LanguagesIsoEnum) => Promise<void>;
}

// Orchestrates the candidate "Change Language" flow on the assessment intro:
// - fetches the assessment's enabled locales (with loading/error surfaced for a page-level gate)
// - applies the determined default (enabledLocales[0]) to the app shell so the intro renders in it
// - auto-opens a non-dismissible modal when the assessment is multi-locale and no choice is persisted
// - persists the choice on Confirm, switching the app locale on success and forcing a retry on failure
// - locks the choice once the attempt has started (no auto-open, no Change link)
export function useCandidateLocaleSelection({
  inviteId,
  inviteLocale,
  hasStarted = false,
  onConfirmed,
  onSelected,
  isContentStale = false,
  hasContentError = false,
  preload,
}: UseCandidateLocaleSelectionArgs) {
  const intl = useIntl();
  const isSurvey = useIsSurvey();
  const { locale: appliedLocale, setLocale } = useLocale();
  const { availableLocales, isLoading, error, retry } = useInviteAvailableLanguagesHook(inviteId);

  const determinedDefault = availableLocales[0];
  const isMultiLocale = availableLocales.length > 1;

  const initialLocale = inviteLocale ?? undefined;
  const [persistedLocale, setPersistedLocale] = useState<LanguagesIsoEnum | undefined>(initialLocale);
  const [selectedLocale, setSelectedLocale] = useState<LanguagesIsoEnum | undefined>(initialLocale);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isApplyingInitialLocale, setIsApplyingInitialLocale] = useState(false);
  const [isLoadingPickedMessages, setIsLoadingPickedMessages] = useState(false);
  const [reportedLocale, setReportedLocale] = useState<LanguagesIsoEnum | undefined>();

  const pendingApplicationRef = useRef<{ locale: LanguagesIsoEnum; whenApplied: Promise<void> } | null>(null);

  /**
   * Switches the app shell to a locale, handing back the application already under way when the same
   * locale is asked for twice. Confirm leans on that sharing: it awaits the very promise the
   * selection started, so it cannot resolve — and the modal cannot close — before the chosen
   * language is the one on screen. A rejected application drops out of the slot so that asking again
   * retries the load instead of handing back the failure forever.
   *
   * @param {LanguagesIsoEnum} locale - the locale to render the shell in
   * @returns {Promise<void>} settles when the shell is rendering that locale; rejects if its message
   *   bundle could not be loaded
   */
  const applyLocale = useCallback(
    (locale: LanguagesIsoEnum) => {
      const pending = pendingApplicationRef.current;
      if (pending?.locale === locale) return pending.whenApplied;

      const whenApplied = setLocale(locale);
      pendingApplicationRef.current = { locale, whenApplied };
      void whenApplied.catch(() => {
        if (pendingApplicationRef.current?.whenApplied === whenApplied) pendingApplicationRef.current = null;
      });
      return whenApplied;
    },
    [setLocale],
  );

  // Once the languages fetch resolves, apply the starting locale to the app shell and, on a
  // multi-locale invite with no saved choice, force the picker open. Runs exactly once, in a layout
  // effect so the modal is open before paint (no frame where Start is reachable un-gated).
  const initializedRef = useRef(false);
  useLayoutEffect(() => {
    if (initializedRef.current) return;
    if (isLoading || error || availableLocales.length === 0) return;
    initializedRef.current = true;

    const startingLocale = persistedLocale ?? determinedDefault;
    if (persistedLocale) onSelected?.(persistedLocale);
    if (startingLocale) {
      setSelectedLocale(startingLocale);
      setIsApplyingInitialLocale(true);
      // A failed bundle load is already reported inside setLocale and leaves the shell on en-US — the
      // same fallback the candidate would have seen. Swallow it here so it isn't an unhandled rejection;
      // Confirm is where a failure has to be surfaced, because that's a choice the candidate made.
      void applyLocale(startingLocale)
        .catch(() => undefined)
        .finally(() => setIsApplyingInitialLocale(false));
    }
    // Don't force the picker open once the attempt has started — the choice is locked for the attempt.
    if (isMultiLocale && !persistedLocale && !hasStarted) {
      setIsModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, error, availableLocales]);

  // Persisting and loading the bundle fail for different reasons and need different copy: once the
  // PATCH lands, the choice *is* saved, and telling the candidate it wasn't sends them chasing the
  // wrong problem. Either way confirm() leaves the modal open, because the attempt must never start
  // on a locale that isn't both persisted and rendered.
  const confirm = useCallback(async () => {
    if (!inviteId || !selectedLocale) return;
    setIsConfirming(true);

    try {
      await InvitesHttpService.updateInviteLocale(inviteId, selectedLocale);
    } catch (err) {
      notification.error({
        message:
          getFriendlyErrorMessage(err) ??
          intl.formatMessage({
            id: "candidate.languageSelect.persistError",
            defaultMessage: "Couldn't save your language choice. Please try again.",
          }),
      });
      setIsConfirming(false);
      return;
    }

    setPersistedLocale(selectedLocale);

    try {
      await applyLocale(selectedLocale);
    } catch {
      notification.error({
        message: intl.formatMessage({
          id: "candidate.languageSelect.languageLoadError",
          defaultMessage: "Your language was saved, but we couldn't load it. Please try again.",
        }),
      });
      setIsConfirming(false);
      return;
    }

    onSelected?.(selectedLocale);
    onConfirmed?.(selectedLocale);
    setIsModalOpen(false);
    setIsConfirming(false);
  }, [inviteId, selectedLocale, applyLocale, intl, onSelected, onConfirmed]);

  /**
   * Records the candidate's pick, loads its message bundle and anything else it preloads, and only
   * then reports it so its content is fetched. The shell switches once that content has arrived, so the modal and the title and
   * intro behind it change language together whichever request is slower. A bundle or content that
   * fails to load leaves everything where it was, the latter with an error toast; Confirm retries the
   * bundle and reports the confirmed locale, so the content always follows what was saved.
   *
   * @param {LanguagesIsoEnum} [locale] - the pick to record and render, or undefined to clear it
   * @returns {void}
   */
  const selectAndApplyLocale = useCallback(
    (locale?: LanguagesIsoEnum) => {
      setSelectedLocale(locale);
      if (!locale) return;
      setIsLoadingPickedMessages(true);
      void Promise.all([loadMessages(locale), preload?.(locale)])
        .then(() => {
          setReportedLocale(locale);
          onSelected?.(locale);
        })
        .catch(() => undefined)
        .finally(() => setIsLoadingPickedMessages(false));
    },
    [onSelected, preload],
  );

  useEffect(() => {
    if (!isModalOpen || !reportedLocale || isContentStale) return;
    void applyLocale(reportedLocale).catch(() => undefined);
  }, [isModalOpen, reportedLocale, isContentStale, applyLocale]);

  const hadContentErrorRef = useRef(false);
  useEffect(() => {
    if (!isModalOpen) return;
    const isNewContentError = hasContentError && !hadContentErrorRef.current;
    hadContentErrorRef.current = hasContentError;
    if (!isNewContentError) return;
    notification.error({
      message: intl.formatMessage(pickAssessmentCopyVariant({ variants: contentLoadErrorMessages, isSurvey })),
    });
  }, [isModalOpen, hasContentError, intl, isSurvey]);

  const openModal = useCallback(() => {
    // The language is locked once the attempt starts; ignore any lingering re-open affordance.
    if (hasStarted) return;
    selectAndApplyLocale(persistedLocale ?? determinedDefault);
    setIsModalOpen(true);
  }, [persistedLocale, determinedDefault, hasStarted, selectAndApplyLocale]);

  return {
    isLoading,
    error,
    retry,
    availableLocales,
    isMultiLocale,
    isApplyingInitialLocale,
    // Whether the candidate may still change the language: multi-locale AND not yet started.
    canChangeLanguage: isMultiLocale && !hasStarted,
    // The locale the intro chrome is currently showing. Read from the shell rather than from the
    // selection: a bundle that fails to load leaves the shell on its previous language, and an
    // indicator that reported the requested locale would then name a language nothing is rendered in.
    currentLocale: appliedLocale,
    persistedLocale,
    isModalOpen,
    selectedLocale,
    selectAndApplyLocale,
    confirm,
    isConfirming,
    isLoadingSelection: isLoadingPickedMessages || (isContentStale && !hasContentError),
    openModal,
  };
}
